import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Select,
  Divider,
  TabButton,
  TextInput,
  FilterMenu,
  DateTimePicker
} from '@sopranodesign/pulse-components';
import {
  FEATURE_GATES,
  FeatureFilter,
  GlobalFeatureGateFilter
} from '@sopranodesign/pulse-auth';
import { AnimateSharedLayout } from '@sopranodesign/framer-motion';
import {
  Conditionals,
  ConditionalContext,
  AddConditionalButton,
  useConditionalReducer
} from '@sopranodesign/pulse-conditionals';
import { subjects as integrationSubjects } from '@sopranodesign/pulse-integrations';

const USER_TYPES = {
  REGULAR: { name: 'Regular', value: 'REGULAR', params: { type: 'regular' } },
  DEMO: { name: 'Demo', value: 'DEMO', params: { type: 'demo' } },
};

const DATE_TYPES = {
  LAST_MESSAGE: { name: 'Last message', value: 'last_active_at' },
  CONVERSATION_STARTED: { name: 'Conversation started', value: 'created_at' },
};

const CONDITIONAL_ENABLES = {
  ENABLE: { name: 'Enable', value: true },
  DISABLE: { name: 'Disable', value: false },
};

const AUTHORISATION_STATUSES = [
  { label: 'Any', value: null },
  { label: 'Authorised', value: true },
  { label: 'Not authorised', value: false },
];

export default function SubscriberFilters({ filters, query }) {
  const conditional = useConditionalReducer({
    subjects: [integrationSubjects.conditionals.variable],
    conditionals: { conditionals: [], match: 'all' },
  });
  const prevConditionalRef = useRef(JSON.stringify([]));
  const handleTypeChange = (option) => filters.updateFilters({ type: option.value });
  const handleAuthorisationStatusChange = (option) => filters.updateFilters({ authorised: option.value });
  const handleNewStart = ([newStart]) => filters.updateFilters({ start: new Date(newStart) });
  const handleNewEnd = ([newEnd]) => filters.updateFilters({ end: new Date(newEnd) });
  const handleDateTypeChange = (type) => filters.updateFilters({ date_type: type.value });
  const handleSubscriberId = (e) => {
    const id = e.target.value;
    id === '' ? filters.deleteEditableFilter('id') : filters.updateFilters({ id });
  };
  const handleKeywordChange = (e) => filters.updateFilters({ keyword: e.target.value });
  const isValidConditional =
    conditional.form.conditionals.length > 0 &&
    conditional.form.conditionals.every((c) => c.type && c.target);

  useEffect(() => {
    if (!filters.filters.hasConditionals || !isValidConditional) {
      if (prevConditionalRef.current !== JSON.stringify([])) {
        filters.updateFilters({ conditionals: [] });
        prevConditionalRef.current = JSON.stringify([]);
      }
      return;
    }

    const newConditional = JSON.stringify(conditional.form.conditionals);
    if (prevConditionalRef.current !== newConditional) {
      filters.updateFilters({ conditionals: conditional.form.conditionals });
      prevConditionalRef.current = newConditional;
    }
  }, [conditional.form.conditionals, filters, isValidConditional]);

  const filterConditionIsValid =
    !filters.filters.hasConditionals ||
    (filters.filters.conditionals.length > 0 &&
      filters.filters.conditionals.every((c) => c.type && c.target));

  const filterMenuIsValid =
    (filters.filters.keyword.length === 0 || filters.filters.keyword.length > 2) &&
    (!filters.filters.id || Number(filters.filters.id) > 0) &&
    filters.filters.start <= filters.filters.end &&
    filterConditionIsValid;

  return (
    <FilterMenu
      position={FilterMenu.POSITIONS.LEFT}
      buttonProps={{ loading: query.isLoading }}
      isValid={filterMenuIsValid}
      {...filters.props}
    >
      <Flex col style={{ width: filters.filters.hasConditionals ? '60rem' : '20rem' }}>
        <Flex col fat>
          <Label htmlFor="type">User type</Label>
          <Select
            id="type"
            value={{
              label: USER_TYPES[filters.filters.type].name,
              value: filters.filters.type,
            }}
            options={Object.values(USER_TYPES).map((type) => ({
              label: type.name,
              value: type.value,
            }))}
            onChange={handleTypeChange}
          />
        </Flex>
        <GlobalFeatureGateFilter for={FEATURE_GATES.AUTHORISATION}>
          <FeatureFilter feature="authorisation">
            <Flex col fat="true" mt>
              <Label htmlFor="authorisation">Authorisation status</Label>
              <Select
                id="authorisation"
                value={AUTHORISATION_STATUSES.find(
                  (status) => filters.filters.authorised === status.value
                )}
                options={AUTHORISATION_STATUSES}
                onChange={handleAuthorisationStatusChange}
              />
            </Flex>
          </FeatureFilter>
        </GlobalFeatureGateFilter>
        <Divider fullWidth />
        <Flex>
          <Flex col>
            <Flex mb col>
              <Label>Filter by</Label>
              <Flex>
                <AnimateSharedLayout>
                  {Object.values(DATE_TYPES).map((type) => (
                    <TabButton
                      key={type.value}
                      active={type.value === filters.filters.date_type}
                      onClick={() => handleDateTypeChange(type)}
                    >
                      {type.name}
                    </TabButton>
                  ))}
                </AnimateSharedLayout>
              </Flex>
            </Flex>
            <Flex col fat="true" mb>
              <Label htmlFor="subscriber-id">Subscriber ID</Label>
              <TextInput
                type="number"
                placeholder="100"
                value={filters.filters.id}
                id="subscriber-id"
                onChange={handleSubscriberId}
              />
            </Flex>
            <Flex col fat="true" mb>
              <Label htmlFor="keyword">Keyword</Label>
              <TextInput
                placeholder="Hello"
                value={filters.filters.keyword}
                id="keyword"
                onChange={handleKeywordChange}
              />
            </Flex>
            <Flex col fat="true" mb>
              <Label htmlFor="start">Start</Label>
              <DateTimePicker id="start" date={filters.filters.start} onChange={handleNewStart} />
            </Flex>
            <Flex col fat="true" mb>
              <Label htmlFor="end">End</Label>
              <DateTimePicker id="end" date={filters.filters.end} onChange={handleNewEnd} />
            </Flex>
            <Flex col fat="true" mb>
              <Label>Enable Conditionals</Label>
              <Flex>
                <AnimateSharedLayout>
                  {Object.values(CONDITIONAL_ENABLES).map((type) => (
                    <TabButton
                      key={type.value}
                      active={type.value === filters.filters.hasConditionals}
                      onClick={() => filters.updateFilters({ hasConditionals: type.value })}
                    >
                      {type.name}
                    </TabButton>
                  ))}
                </AnimateSharedLayout>
              </Flex>
            </Flex>
          </Flex>
          {filters.filters.hasConditionals && (
            <Flex col fat="true" pl>
              <Label>Conditionals</Label>
              <ConditionalContext.Provider value={conditional}>
                <Flex grow col>
                  <Conditionals labelPrefix="conversation-conditionals" />
                  <Flex right>
                    <AddConditionalButton />
                  </Flex>
                </Flex>
              </ConditionalContext.Provider>
            </Flex>
          )}
        </Flex>
      </Flex>
    </FilterMenu>
  );
}

SubscriberFilters.propTypes = {
  filters: PropTypes.shape({
    filters: PropTypes.shape({
      type: PropTypes.string.isRequired,
      authorised: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
      date_type: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      keyword: PropTypes.string,
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date),
      conditionals: PropTypes.arrayOf(PropTypes.object),
      hasConditionals: PropTypes.bool.isRequired,
    }).isRequired,
    updateFilters: PropTypes.func.isRequired,
    deleteEditableFilter: PropTypes.func.isRequired,
    props: PropTypes.object,
  }).isRequired,
  query: PropTypes.shape({
    isLoading: PropTypes.bool,
  }).isRequired,
};